/**
 * Daily Gift Service Module
 *
 * Handles daily gift claiming with server-side validation.
 */

import { supabase } from '../supabase';

/**
 * Daily gift claim result
 */
export interface DailyGiftResult {
  success: boolean;
  message: string;
  xpAwarded?: number;
  newXp?: number;
  boostActivated?: boolean;
  boostExpiresAt?: number;
  boostMultiplier?: number;
  alreadyClaimed?: boolean;
}

/**
 * Claim daily gift with server-side validation
 *
 * SECURITY: Uses server-side validation to:
 * - Prevent claiming multiple times per day
 * - Atomically award XP and update last claim date
 * - Optionally activate pomodoro boost
 * - Sync claim status across all devices
 *
 * @param userId - User's UUID
 * @param xpAmount - XP to award for this gift
 * @param activateBoost - If true, activate XP boost
 * @param boostDurationHours - Hours the boost lasts (default 24)
 * @param boostMultiplier - XP multiplier (default 1.25 = +25%)
 * @returns Result with success status and new XP value
 */
export async function claimDailyGift(
  userId: string,
  xpAmount: number,
  activateBoost: boolean = false,
  boostDurationHours: number = 24,
  boostMultiplier: number = 1.25
): Promise<DailyGiftResult> {
  if (import.meta.env.DEV) {
    console.log(
      `[User Sync] Claiming daily gift for user ${userId} (XP: ${xpAmount}, Boost: ${activateBoost}, Multiplier: ${boostMultiplier})`
    );
  }

  // Always use Supabase Auth with auth.uid()
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    throw new Error('Authentication required to claim daily gift');
  }

  if (import.meta.env.DEV) console.log('[User Sync] Using authenticated session');

  const { data, error } = await supabase.rpc('claim_daily_gift', {
    p_user_id: userId,
    p_xp_amount: xpAmount,
    p_activate_boost: activateBoost,
    p_boost_duration_hours: boostDurationHours,
    p_boost_multiplier: boostMultiplier,
  });

  if (error) {
    console.error('[User Sync] Error claiming daily gift:', error);
    throw new Error(`Failed to claim daily gift: ${error.message}`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const resultData = data as any;

  if (!resultData.success) {
    if (import.meta.env.DEV) console.log('[User Sync] Daily gift already claimed today');
  } else {
    if (import.meta.env.DEV)
      console.log(
        `[User Sync] Daily gift claimed successfully - ${resultData.xp_awarded} XP awarded`
      );
    if (resultData.boost_activated && import.meta.env.DEV) {
      console.log(
        `[User Sync] Boost activated: ${resultData.boost_multiplier}x until ${new Date(resultData.boost_expires_at)}`
      );
    }
  }

  // Convert boost_expires_at to milliseconds timestamp
  let boostExpiresAtMs: number | undefined = undefined;
  if (resultData.boost_expires_at) {
    if (typeof resultData.boost_expires_at === 'string') {
      // ISO timestamp string - convert to milliseconds
      boostExpiresAtMs = new Date(resultData.boost_expires_at).getTime();
    } else if (typeof resultData.boost_expires_at === 'number') {
      // Check if seconds or milliseconds
      boostExpiresAtMs =
        resultData.boost_expires_at > 100000000000
          ? resultData.boost_expires_at
          : resultData.boost_expires_at * 1000;
    }
  }

  if (import.meta.env.DEV) {
    console.log('[User Sync] Boost expires at:', {
      raw: resultData.boost_expires_at,
      converted: boostExpiresAtMs,
      activated: resultData.boost_activated,
    });
  }

  return {
    success: resultData.success,
    message: resultData.message,
    xpAwarded: resultData.xp_awarded,
    newXp: resultData.new_xp,
    boostActivated: resultData.boost_activated || false,
    boostExpiresAt: boostExpiresAtMs,
    boostMultiplier: resultData.boost_multiplier,
    alreadyClaimed: resultData.already_claimed || false,
  };
}

/**
 * Check if user can claim daily gift today
 *
 * @param userId - User's UUID
 * @returns true if gift can be claimed, false if already claimed today
 */
export async function canClaimDailyGift(userId: string): Promise<boolean> {
  if (import.meta.env.DEV)
    console.log(`[User Sync] Checking if user ${userId} can claim daily gift`);

  // Always use Supabase Auth with auth.uid()
  const {
    data: { session },
  } = await supabase.auth.getSession();

  if (!session) {
    // If no session, they can't claim (or we can't verify)
    return false;
  }

  const result = await supabase.rpc('can_claim_daily_gift', {
    p_user_id: userId,
  });

  const { data, error } = result;

  if (error) {
    console.error('[User Sync] Error checking gift eligibility:', error);
    return false; // Fail safe - don't allow claim if we can't verify
  }

  return data as boolean;
}
